import { useMutation, useQuery } from "@tanstack/react-query";

import { QUERY_NAME, SERVICES } from "@lhd/constants";
import axios from "@lhd/libs/axios";
import { Permission } from "@lhd/types";
import { getAccessToken, getCompanyId } from "@lhd/utility";
import { notification } from "antd";
import { ELearningPermissionObject } from "types/elearningCourse.type";
import { FilterParams } from "@lhd/features/Salary/AnnualPermissions/ELearningPermissions/hook";
import { normalizeSavePayloadRecursively } from "@lhd/utility";

export const useFetchELearningPermissions = (page = 1, pageSize = 1000) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.E_LEARNING_PERMISSIONS, companyId, page, pageSize],
    () =>
      axios({
        method: "get",
        url: SERVICES.E_LEARNING_PERMISSIONS,
        params: {
          page,
          per_page: pageSize,
          company_id: companyId,
        },
      }).then((res) => ({
        data: res.data as Permission[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 100,
      staleTime: 100,
    },
  );
};

export const useFetchDefaultELearningPermissions = (
  page = 1,
  pageSize = 1000,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.E_LEARNING_DEFAULT_PERMISSIONS, companyId, page, pageSize],
    () =>
      axios({
        method: "get",
        url: SERVICES.E_LEARNING_PERMISSIONS + "/default_permissions",
        params: {
          page,
          per_page: pageSize,
          company_id: companyId,
        },
      }).then((res) => ({
        data: res.data,
      })),
    {
      cacheTime: 100,
      staleTime: 100,
    },
  );
};

export const useFetchELearningCourses = (
  page: number,
  pageSize: number,
  filterParams?: FilterParams,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [
      QUERY_NAME.E_LEARNING_COURSES_WITH_PERMISSIONS,
      companyId,
      page,
      pageSize,
      filterParams,
    ],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.E_LEARNING_PERMISSIONS}/courses`,
        params: {
          page,
          per_page: pageSize,
          company_id: companyId,
          ...filterParams,
        },
      }).then((res) => ({
        data: res.data,
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 100,
      staleTime: 100,
    },
  );
};

export const useSubmitELearningPermission = () => {
  const companyId = getCompanyId();

  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.E_LEARNING_PERMISSIONS}/save`,
        data: {
          ...normalizeSavePayloadRecursively(payload),
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useFetchCurrentUserELearningPermissions = (
  enabledOverride = true,
) => {
  const companyId = getCompanyId();
  const accessToken = getAccessToken();
  return useQuery(
    [QUERY_NAME.E_LEARNING_CURRENT_USER_PERMISSIONS, companyId],
    () =>
      axios({
        method: "get",
        url: SERVICES.E_LEARNING_PERMISSIONS + "/current_user",
        params: {
          company_id: companyId,
        },
      }).then((res) => ({
        data: res.data as ELearningPermissionObject,
      })),
    {
      cacheTime: 100,
      staleTime: 100,
      enabled: enabledOverride && !!companyId && !!accessToken,
    },
  );
};
